import { useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Bold, Italic } from "lucide-react";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  onSave?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Converts markdown to HTML for editing
 */
const markdownToHtml = (markdown: string): string => {
  if (!markdown) return "";
  // Convert **text** to <strong>text</strong>
  return markdown.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
};

/**
 * Converts HTML back to markdown
 */
const htmlToMarkdown = (html: string): string => {
  if (!html) return "";
  // Convert <strong>text</strong> to **text**
  let markdown = html.replace(/<strong>(.*?)<\/strong>/gi, "**$1**");
  // Remove other HTML tags but keep text
  markdown = markdown.replace(/<[^>]*>/g, "");
  // Decode HTML entities
  const textarea = document.createElement("textarea");
  textarea.innerHTML = markdown;
  markdown = textarea.value;
  // Clean up extra whitespace
  markdown = markdown.replace(/\n\s*\n\s*\n/g, "\n\n");
  return markdown.trim();
};

export const RichTextEditor = ({
  value,
  onChange,
  onSave,
  placeholder = "Write a summary for this listing...",
  disabled = false,
  className = "",
}: RichTextEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null);

  // Initialize HTML content from markdown
  useEffect(() => {
    const html = markdownToHtml(value);
    if (editorRef.current && editorRef.current.innerHTML !== html) {
      // Only update if content actually changed to avoid cursor jumping
      const selection = window.getSelection();
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
      const cursorPosition = range?.startOffset || 0;
      
      editorRef.current.innerHTML = html;
      
      // Try to restore cursor position
      if (range && editorRef.current.firstChild) {
        try {
          const newRange = document.createRange();
          newRange.setStart(editorRef.current.firstChild, Math.min(cursorPosition, editorRef.current.firstChild.textContent?.length || 0));
          newRange.collapse(true);
          selection?.removeAllRanges();
          selection?.addRange(newRange);
        } catch (e) {
          // If cursor restoration fails, just move to end
          const newRange = document.createRange();
          newRange.selectNodeContents(editorRef.current);
          newRange.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(newRange);
        }
      }
    }
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Handle Cmd+B (Mac) or Ctrl+B (Windows/Linux) for bold
    if ((e.metaKey || e.ctrlKey) && e.key === "b") {
      e.preventDefault();
      document.execCommand("bold", false);
      return;
    }

    // Handle Cmd+I (Mac) or Ctrl+I (Windows/Linux) for italic
    if ((e.metaKey || e.ctrlKey) && e.key === "i") {
      e.preventDefault();
      document.execCommand("italic", false);
      return;
    }
  };

  const handleBold = () => {
    document.execCommand("bold", false);
    editorRef.current?.focus();
  };

  const handleItalic = () => {
    document.execCommand("italic", false);
    editorRef.current?.focus();
  };

  const handleInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      // Auto-save on input
      const markdown = htmlToMarkdown(html);
      onChange(markdown);
    }
  };

  const handleBlur = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      const markdown = htmlToMarkdown(html);
      onChange(markdown);
      // Auto-save on blur if onSave callback is provided
      if (onSave && markdown !== value) {
        onSave(markdown);
      }
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 p-2 border-b bg-muted/30 rounded-t-md">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBold}
          className="h-8 w-8 p-0"
          title="Bold (⌘B)"
          disabled={disabled}
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleItalic}
          className="h-8 w-8 p-0"
          title="Italic (⌘I)"
          disabled={disabled}
        >
          <Italic className="h-4 w-4" />
        </Button>
        <div className="flex-1" />
        <div className="text-xs text-muted-foreground">
          ⌘B for bold, ⌘I for italic
        </div>
      </div>
      <div
        ref={editorRef}
        contentEditable={!disabled}
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        onBlur={handleBlur}
        className={`min-h-32 p-4 rounded-b-md border border-input bg-background text-foreground leading-relaxed text-base focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${className}`}
        style={{
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
        }}
        suppressContentEditableWarning={true}
        data-placeholder={placeholder}
      />
    </div>
  );
};
